// Run Detail Drawer — Slide-out panel for audit log entries
import { useState, useEffect } from 'react';
import { authFetch, API_URL } from '../hooks/useAuth';

export const RunDetailDrawer = ({ run, onClose, webhookEvents }) => {
  const [fullRun, setFullRun] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch full run details (tokens + error) on open
  useEffect(() => {
    if (!run?.id) return;
    setLoading(true);
    authFetch(`${API_URL}/api/audit/${run.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setFullRun(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [run?.id]);

  // Merge list data with full detail data
  const r = fullRun || run;
  if (!r) return null;

  const statusOk = ['success', 'deployed', 'complete', 'rollback'].includes(r.status);
  const isRollback = r.status === 'rollback';
  const isPreview = r.dry_run;
  const runType = isRollback ? 'Rollback' : isPreview ? 'Safe Preview' : 'Live Sync';

  // Duration
  let duration = null;
  if (r.started_at && r.completed_at) {
    const ms = new Date(r.completed_at) - new Date(r.started_at);
    if (ms > 0) {
      const secs = Math.floor(ms / 1000);
      duration = secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
    }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) : '\u2014';

  const hasTokensBefore = r.tokens_before && r.tokens_before.length > 10;
  const hasTokensAfter = r.tokens_after && r.tokens_after.length > 10;

  // Try to find matching webhook event with PR info
  const match = (webhookEvents || []).find(e =>
    e.action === 'sync' && e.status === 'success' && e.deploy_result?.includes(r.run_id?.substring(0, 8))
  ) || (webhookEvents || []).find(e =>
    e.action === 'sync' && e.status === 'success' && e.pr_number
  );
  const prInfo = match?.pr_number ? { number: match.pr_number, result: match.deploy_result } : null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="drawer-backdrop"
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          zIndex: 999, opacity: 1, transition: 'opacity 0.2s ease'
        }}
      />
      {/* Drawer */}
      <div
        data-testid="run-detail-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(480px, 90vw)', zIndex: 1000,
          background: 'var(--beige-100, #f5f0e8)',
          borderLeft: '2px solid rgba(180,178,170,0.4)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          animation: 'slideInRight 0.25s ease-out',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid rgba(180,178,170,0.3)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'var(--beige-100, #f5f0e8)', zIndex: 1
        }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary, #2a2520)' }}>
              Run Details
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted, #8a8680)', marginTop: '2px', fontFamily: "'SF Mono','Fira Code',monospace" }}>
              {r.run_id || '\u2014'}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close-btn"
            style={{
              background: 'none', border: '1px solid rgba(180,178,170,0.4)',
              borderRadius: '6px', padding: '4px 10px', cursor: 'pointer',
              fontSize: '13px', color: 'var(--text-muted, #8a8680)',
            }}
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Loading indicator */}
          {loading && (
            <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>
              Loading details...
            </div>
          )}

          {/* Status banner */}
          <div style={{
            padding: '12px 16px', borderRadius: '8px',
            background: statusOk ? 'rgba(22,163,106,0.08)' : 'rgba(220,38,38,0.08)',
            border: `1px solid ${statusOk ? 'rgba(22,163,106,0.2)' : 'rgba(220,38,38,0.2)'}`,
            display: 'flex', alignItems: 'center', gap: '10px'
          }} data-testid="drawer-status-banner">
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: statusOk ? 'rgba(22,163,106,0.15)' : 'rgba(220,38,38,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px'
            }}>
              {isRollback ? '\u21A9' : statusOk ? '\u2713' : '\u2717'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: statusOk ? '#16a34a' : '#dc2626', textTransform: 'capitalize' }}>
                {r.status}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{runType}</div>
            </div>
            {duration && (
              <div style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary, #5a5550)' }}>
                {duration}
              </div>
            )}
          </div>

          {/* Metadata grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <DetailField label="Store" value={r.store_domain || r.store_id || r.store_name || '\u2014'} testId="drawer-store" />
            <DetailField label="Run Type" value={runType} testId="drawer-run-type" />
            <DetailField label="Started" value={fmtDate(r.started_at)} testId="drawer-started" />
            <DetailField label="Completed" value={fmtDate(r.completed_at)} testId="drawer-completed" />
            <DetailField label="Runs Used After" value={r.runs_used_after ?? '\u2014'} testId="drawer-runs-used" />
            {prInfo?.number && (
              <DetailField label="Pull Request" testId="drawer-pr">
                <span style={{ fontFamily: "'SF Mono','Fira Code',monospace", fontSize: '12px' }}>
                  PR #{prInfo.number}
                </span>
              </DetailField>
            )}
          </div>

          {/* PR result */}
          {prInfo?.result && (
            <div style={{
              padding: '10px 14px', borderRadius: '6px',
              background: 'rgba(124,106,72,0.06)', border: '1px solid rgba(180,178,170,0.2)',
              fontSize: '12px', color: 'var(--text-secondary, #5a5550)',
              fontFamily: "'SF Mono','Fira Code',monospace", wordBreak: 'break-all'
            }} data-testid="drawer-pr-result">
              {prInfo.result}
            </div>
          )}

          {/* Error details for failed runs */}
          {!statusOk && r.error_message && (
            <div data-testid="drawer-error">
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Error</div>
              <div style={{
                padding: '10px 14px', borderRadius: '6px',
                background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.15)',
                fontSize: '12px', color: '#991b1b', fontFamily: "'SF Mono','Fira Code',monospace",
                whiteSpace: 'pre-wrap', wordBreak: 'break-word'
              }}>
                {r.error_message}
              </div>
            </div>
          )}

          {/* Rollback info */}
          {isRollback && (
            <div data-testid="drawer-rollback-info" style={{
              padding: '12px 14px', borderRadius: '6px',
              background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)',
              fontSize: '12px', color: '#5b21b6'
            }}>
              <div style={{ fontWeight: 700, marginBottom: '4px' }}>Rollback Operation</div>
              <div style={{ color: 'var(--text-secondary, #5a5550)' }}>
                This run restored a previous version of <code>tokens.css</code> to the Shopify theme.
                {hasTokensBefore && ' The tokens that were active before the rollback are preserved below.'}
              </div>
            </div>
          )}

          {/* Tokens preview */}
          {(hasTokensBefore || hasTokensAfter) && (
            <TokensPreview
              label={isRollback ? 'Restored Tokens' : 'Generated Tokens'}
              tokens={isRollback ? r.tokens_before : r.tokens_after}
              testId="drawer-tokens"
            />
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

const DetailField = ({ label, value, testId, children }) => (
  <div data-testid={testId}>
    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted, #8a8680)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
      {label}
    </div>
    <div style={{ fontSize: '13px', color: 'var(--text-primary, #2a2520)', wordBreak: 'break-word' }}>
      {children || value}
    </div>
  </div>
);

const TokensPreview = ({ label, tokens, testId }) => {
  const [expanded, setExpanded] = useState(false);
  if (!tokens || tokens.length < 10) return null;
  const preview = expanded ? tokens : tokens.substring(0, 300);

  return (
    <div data-testid={testId}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted, #8a8680)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{
        padding: '10px 14px', borderRadius: '6px',
        background: 'rgba(26,26,46,0.04)', border: '1px solid rgba(180,178,170,0.2)',
        fontSize: '11px', fontFamily: "'SF Mono','Fira Code',monospace",
        color: 'var(--text-secondary, #5a5550)', whiteSpace: 'pre-wrap',
        maxHeight: expanded ? '400px' : '80px', overflow: 'auto',
        transition: 'max-height 0.3s ease'
      }}>
        {preview}{!expanded && tokens.length > 300 ? '...' : ''}
      </div>
      {tokens.length > 300 && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            background: 'none', border: 'none', color: 'var(--accent, #7c6a48)',
            fontSize: '11px', cursor: 'pointer', padding: '4px 0', fontWeight: 600
          }}
          data-testid={`${testId}-toggle`}
        >
          {expanded ? 'Show less' : `Show all (${(tokens.length / 1024).toFixed(1)} KB)`}
        </button>
      )}
    </div>
  );
};
